import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from '../components/ui/dialog';
import { Plus, FolderOpen, FileText, Lock, Buildings, Warning, ListBullets, SquaresFour } from '@phosphor-icons/react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const Dashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [projects, setProjects] = useState([]);
  const [overview, setOverview] = useState({});
  const [viewMode, setViewMode] = useState('list');
  const [sortBy, setSortBy] = useState('newest');
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [clientName, setClientName] = useState('');
  const [description, setDescription] = useState('');
  const [companyId, setCompanyId] = useState('');
  const [companies, setCompanies] = useState([]);
  const [clients, setClients] = useState([]);
  const [selectedClientId, setSelectedClientId] = useState('');
  const [selectedBrandId, setSelectedBrandId] = useState('');
  const [pcfScope, setPcfScope] = useState('');
  const [pcfLocation, setPcfLocation] = useState('');
  const [pcfTimeline, setPcfTimeline] = useState('');
  const [pcfSpecialRequirements, setPcfSpecialRequirements] = useState('');
  const [pcfBypassed, setPcfBypassed] = useState(false);
  const [showPcf, setShowPcf] = useState(true);
  const [clientOutstanding, setClientOutstanding] = useState(null);
  const [outstandingAcknowledged, setOutstandingAcknowledged] = useState(false);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [projectsRes, companiesRes, clientsRes, overviewRes, prefsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/projects`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/companies`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/clients`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/projects-overview`, { withCredentials: true }).catch(() => ({ data: { projects: [] } })),
        axios.get(`${BACKEND_URL}/api/users/me/preferences`, { withCredentials: true }).catch(() => ({ data: {} })),
      ]);
      setProjects(projectsRes.data);
      setCompanies(companiesRes.data);
      setClients(clientsRes.data);
      const ovMap = {};
      (overviewRes.data.projects || []).forEach(o => { ovMap[o.id] = o; });
      setOverview(ovMap);
      if (prefsRes.data?.projects_view) setViewMode(prefsRes.data.projects_view);
      if (prefsRes.data?.projects_sort) setSortBy(prefsRes.data.projects_sort);
    } catch { toast.error('Failed to load data'); }
    finally { setLoading(false); }
  };

  const savePref = (patch) => {
    axios.put(`${BACKEND_URL}/api/users/me/preferences`, patch, { withCredentials: true }).catch(() => {});
  };
  const changeView = (v) => { setViewMode(v); savePref({ projects_view: v }); };
  const changeSort = (s) => { setSortBy(s); savePref({ projects_sort: s }); };

  const HEALTH_DOT = { green: 'bg-emerald-500', amber: 'bg-amber-500', red: 'bg-red-500', unknown: 'bg-zinc-300' };
  const fmtINR = (n) => `₹${Math.round(n || 0).toLocaleString('en-IN')}`;

  const sortedProjects = [...projects].sort((a, b) => {
    const oa = overview[a.id] || {}, ob = overview[b.id] || {};
    switch (sortBy) {
      case 'highest_risk': return (ob.open_alerts_count || 0) - (oa.open_alerts_count || 0);
      case 'worst_margin': return (oa.margin_pct ?? 999) - (ob.margin_pct ?? 999);
      case 'highest_outstanding': return (ob.outstanding_receivable || 0) - (oa.outstanding_receivable || 0);
      case 'worst_reporting': return (ob.days_since_last_dpr ?? -1) - (oa.days_since_last_dpr ?? -1);
      default: return new Date(ob.created_at || b.created_at || 0) - new Date(oa.created_at || a.created_at || 0);
    }
  });

  const handleCreateProject = async (e) => {
    e.preventDefault();
    if (!selectedClientId) {
      toast.error('Please select a client');
      return;
    }
    // Derive client_name from selected client
    const selectedClient = clients.find(c => c.id === selectedClientId);
    const derivedName = selectedBrandId
      ? (selectedClient?.brands?.find(b => b.id === selectedBrandId)?.name || selectedClient?.legal_name || clientName)
      : (selectedClient?.legal_name || clientName);
    try {
      const payload = {
        client_name: derivedName,
        description,
        company_id: companyId,
        client_id: selectedClientId,
        brand_id: selectedBrandId || null,
        pcf_scope: pcfScope || null,
        pcf_location: pcfLocation || null,
        pcf_timeline: pcfTimeline || null,
        pcf_special_requirements: pcfSpecialRequirements || null,
        pcf_bypassed: pcfBypassed,
      };
      const response = await axios.post(`${BACKEND_URL}/api/projects`, payload, { withCredentials: true });
      toast.success(`Project created! PI: ${response.data.pi_number}`);
      setDialogOpen(false);
      setClientName(''); setDescription(''); setSelectedClientId(''); setSelectedBrandId('');
      setPcfScope(''); setPcfLocation(''); setPcfTimeline(''); setPcfSpecialRequirements('');
      setPcfBypassed(false); setShowPcf(true);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create project');
    }
  };

  const selectedClient = clients.find(c => c.id === selectedClientId);
  const clientBrands = selectedClient?.brands || [];

  // Check client outstanding when client is selected
  useEffect(() => {
    if (!selectedClientId) { setClientOutstanding(null); setOutstandingAcknowledged(false); return; }
    axios.get(`${BACKEND_URL}/api/clients/${selectedClientId}/outstanding`, { withCredentials: true })
      .then(r => { setClientOutstanding(r.data); setOutstandingAcknowledged(false); })
      .catch(() => setClientOutstanding(null));
  }, [selectedClientId]);

  const handleStatusUpdate = async (projectId, newStatus) => {
    try {
      const response = await axios.put(
        `${BACKEND_URL}/api/projects/${projectId}/status`,
        { status: newStatus },
        { withCredentials: true }
      );
      if (newStatus === 'Confirmed' && response.data.project_code) {
        toast.success(`Confirmed! Project Code: ${response.data.project_code}`);
      } else {
        toast.success('Status updated');
      }
      fetchData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to update status'); }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Confirmed': return 'bg-green-100 text-green-800 border-green-300';
      case 'Interim Approval': return 'bg-amber-100 text-amber-800 border-amber-300';
      case 'On Hold': return 'bg-orange-100 text-orange-800 border-orange-300';
      case 'Client Not Reverting': return 'bg-rose-100 text-rose-800 border-rose-300';
      case 'Cancelled': return 'bg-red-100 text-red-800 border-red-300';
      case 'Draft': return 'bg-zinc-100 text-zinc-600 border-zinc-300';
      default: return 'bg-zinc-100 text-zinc-600 border-zinc-300';
    }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center bg-zinc-50"><div className="animate-pulse text-zinc-600">Loading...</div></div>;
  }

  return (
    <div>
      <main className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h2 className="text-xl font-semibold tracking-tight text-zinc-800">Projects</h2>
            <p className="text-sm text-zinc-600 mt-1">{projects.length} total</p>
          </div>
          <div className="flex items-center gap-3">
            <select value={sortBy} onChange={(e) => changeSort(e.target.value)}
              className="text-xs border border-zinc-200 rounded-none px-2 py-2 bg-white" data-testid="projects-sort">
              <option value="newest">Newest first</option>
              <option value="highest_risk">Highest risk</option>
              <option value="worst_margin">Worst margin</option>
              <option value="highest_outstanding">Highest outstanding</option>
              <option value="worst_reporting">Worst reporting</option>
            </select>
            <div className="flex border border-zinc-200">
              <button onClick={() => changeView('list')} className={`px-2.5 py-2 ${viewMode === 'list' ? 'bg-zinc-800 text-white' : 'bg-white text-zinc-500'}`} data-testid="view-toggle-list" title="List view"><ListBullets size={16} /></button>
              <button onClick={() => changeView('card')} className={`px-2.5 py-2 ${viewMode === 'card' ? 'bg-zinc-800 text-white' : 'bg-white text-zinc-500'}`} data-testid="view-toggle-card" title="Card view"><SquaresFour size={16} /></button>
            </div>
            {['admin', 'super_admin', 'estimator', 'project_team'].includes(user.role) && (
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button className="bg-blue-700 hover:bg-blue-800 text-white rounded-none" data-testid="create-project-button">
                  <Plus size={18} className="mr-1.5" /> New Project
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-white border border-zinc-200 rounded-none shadow-xl">
                <DialogHeader>
                  <DialogTitle className="text-xl font-bold text-zinc-900">Create New Project</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleCreateProject} className="space-y-4 mt-3">
                  <div>
                    <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">Company *</Label>
                    <select value={companyId} onChange={(e) => setCompanyId(e.target.value)} required
                      className="mt-1 w-full bg-zinc-50 border border-zinc-200 rounded-none p-2.5 text-sm" data-testid="company-select">
                      <option value="">Select Company</option>
                      {companies.map((c) => <option key={c.id} value={c.id}>{c.name} ({c.state})</option>)}
                    </select>
                    {companies.length === 0 && <p className="text-xs text-red-600 mt-1">No companies found. Add a company first.</p>}
                  </div>
                  <div>
                    <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">Client *</Label>
                    <select value={selectedClientId} onChange={(e) => { setSelectedClientId(e.target.value); setSelectedBrandId(''); }} required
                      className="mt-1 w-full bg-zinc-50 border border-zinc-200 rounded-none p-2.5 text-sm" data-testid="client-select">
                      <option value="">Select Client</option>
                      {clients.map((c) => <option key={c.id} value={c.id}>{c.legal_name} ({c.client_code})</option>)}
                    </select>
                    {clients.length === 0 && <p className="text-xs text-amber-600 mt-1">No clients registered. <span className="underline cursor-pointer" onClick={() => { setDialogOpen(false); navigate('/clients'); }}>Add client first</span></p>}
                  </div>
                  {clientBrands.length > 0 && (
                    <div>
                      <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">Brand (Optional)</Label>
                      <select value={selectedBrandId} onChange={(e) => setSelectedBrandId(e.target.value)}
                        className="mt-1 w-full bg-zinc-50 border border-zinc-200 rounded-none p-2.5 text-sm" data-testid="brand-select">
                        <option value="">Tag to Parent Entity</option>
                        {clientBrands.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                      </select>
                    </div>
                  )}
                  <div className="text-xs text-zinc-600 bg-blue-50 border border-blue-200 p-2.5">
                    PI Number auto-generated on creation. Project Code assigned only when confirmed.
                  </div>
                  <div>
                    <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">Description</Label>
                    <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={2}
                      className="mt-1 w-full bg-zinc-50 border border-zinc-200 rounded-none p-2.5 text-sm" data-testid="description-input" />
                  </div>

                  {/* PCF Section */}
                  <div className="border border-zinc-200 bg-zinc-50 p-3">
                    <div className="flex items-center justify-between mb-2">
                      <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">Project Consultation Form (PCF)</Label>
                      {user.role === 'admin' && (
                        <label className="flex items-center gap-2 text-xs text-zinc-600 cursor-pointer">
                          <input type="checkbox" checked={pcfBypassed} onChange={(e) => { setPcfBypassed(e.target.checked); if (e.target.checked) setShowPcf(false); else setShowPcf(true); }} className="w-3.5 h-3.5" />
                          Bypass PCF (Super Admin)
                        </label>
                      )}
                    </div>
                    {showPcf && !pcfBypassed && (
                      <div className="space-y-3">
                        <div>
                          <Label className="text-xs text-zinc-500">Scope of Work</Label>
                          <textarea value={pcfScope} onChange={(e) => setPcfScope(e.target.value)} rows={2}
                            className="mt-1 w-full bg-white border border-zinc-200 rounded-none p-2.5 text-sm" data-testid="pcf-scope" />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <Label className="text-xs text-zinc-500">Location</Label>
                            <Input value={pcfLocation} onChange={(e) => setPcfLocation(e.target.value)} className="rounded-none mt-1" data-testid="pcf-location" />
                          </div>
                          <div>
                            <Label className="text-xs text-zinc-500">Timeline</Label>
                            <Input value={pcfTimeline} onChange={(e) => setPcfTimeline(e.target.value)} className="rounded-none mt-1" placeholder="e.g., 3 months" data-testid="pcf-timeline" />
                          </div>
                        </div>
                        <div>
                          <Label className="text-xs text-zinc-500">Special Requirements</Label>
                          <textarea value={pcfSpecialRequirements} onChange={(e) => setPcfSpecialRequirements(e.target.value)} rows={2}
                            className="mt-1 w-full bg-white border border-zinc-200 rounded-none p-2.5 text-sm" data-testid="pcf-special" />
                        </div>
                      </div>
                    )}
                    {pcfBypassed && (
                      <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 p-2 mt-1">PCF bypassed by Super Admin. This will be recorded in the audit log.</p>
                    )}
                  </div>

                  {/* Client Outstanding Warning */}
                  {clientOutstanding && clientOutstanding.has_outstanding && !outstandingAcknowledged && (
                    <div className="bg-red-50 border-2 border-red-300 p-3" data-testid="client-outstanding-warning">
                      <div className="flex items-start gap-2">
                        <Warning size={20} className="text-red-600 mt-0.5 shrink-0" />
                        <div className="flex-1">
                          <p className="text-sm font-bold text-red-700">Client has unpaid invoices</p>
                          <p className="text-xs text-red-600 mt-1">Outstanding: Rs {(clientOutstanding.total_outstanding || 0).toLocaleString('en-IN')}</p>
                          <div className="flex gap-3 text-[10px] text-red-500 mt-1">
                            <span>0-30d: Rs {(clientOutstanding.ageing?.['0_30'] || 0).toLocaleString('en-IN')}</span>
                            <span>30-60d: Rs {(clientOutstanding.ageing?.['30_60'] || 0).toLocaleString('en-IN')}</span>
                            <span>60-90d: Rs {(clientOutstanding.ageing?.['60_90'] || 0).toLocaleString('en-IN')}</span>
                            <span>90+: Rs {(clientOutstanding.ageing?.['90_plus'] || 0).toLocaleString('en-IN')}</span>
                          </div>
                          <Button type="button" onClick={() => setOutstandingAcknowledged(true)} size="sm"
                            className="mt-2 bg-red-700 hover:bg-red-800 text-white rounded-none text-xs h-7" data-testid="acknowledge-outstanding-btn">
                            I acknowledge — proceed anyway
                          </Button>
                        </div>
                      </div>
                    </div>
                  )}
                  {clientOutstanding && clientOutstanding.has_outstanding && outstandingAcknowledged && (
                    <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 p-2">Outstanding of Rs {(clientOutstanding.total_outstanding || 0).toLocaleString('en-IN')} acknowledged. Project creation allowed.</p>
                  )}

                  <Button type="submit" className="w-full bg-blue-700 hover:bg-blue-800 text-white rounded-none py-5"
                    disabled={clientOutstanding?.has_outstanding && !outstandingAcknowledged}
                    data-testid="submit-project-button">
                    Create Project
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          )}
          </div>
        </div>

        {projects.length === 0 ? (
          <div className="border border-zinc-200 bg-white p-12 text-center">
            <FolderOpen size={48} className="mx-auto text-zinc-300 mb-3" />
            <p className="text-base font-medium text-zinc-600">No projects yet</p>
            <p className="text-sm text-zinc-500 mt-1">Create your first project to get started. Each project auto-generates a PI number and tracks through Draft to Execution.</p>
          </div>
        ) : viewMode === 'list' ? (
          <div className="bg-white border border-zinc-200 overflow-x-auto" data-testid="projects-list-view">
            <table className="w-full text-xs">
              <thead className="bg-zinc-50 border-b border-zinc-200 text-zinc-500 uppercase text-[10px]">
                <tr>
                  <th className="text-left px-3 py-2.5">PI / Client</th>
                  <th className="text-right px-3 py-2.5">Value</th>
                  <th className="text-center px-3 py-2.5">Completion</th>
                  <th className="text-right px-3 py-2.5">Outstanding</th>
                  <th className="text-center px-3 py-2.5">Alerts</th>
                  <th className="text-center px-3 py-2.5">Margin</th>
                  <th className="text-center px-3 py-2.5">Last DPR</th>
                  <th className="text-left px-3 py-2.5">Status</th>
                </tr>
              </thead>
              <tbody>
                {sortedProjects.map((project) => {
                  const o = overview[project.id] || {};
                  return (
                    <tr key={project.id} onClick={() => navigate(`/projects/${project.id}`)}
                      className="border-b border-zinc-100 hover:bg-zinc-50 cursor-pointer" data-testid={`project-row-${project.id}`}>
                      <td className="px-3 py-2.5">
                        <div className="font-semibold text-zinc-800 flex items-center gap-1.5">
                          {project.pi_number}
                          {(overview[project.id] || {}).is_demo && ['admin', 'super_admin'].includes(user.role) && (
                            <span className="text-[8px] bg-amber-100 text-amber-700 px-1 py-0.5 rounded font-bold uppercase" data-testid={`demo-badge-${project.id}`}>Demo</span>
                          )}
                        </div>
                        <div className="text-zinc-500">{project.client_name}</div>
                      </td>
                      <td className="px-3 py-2.5 text-right font-medium text-zinc-700">{o.project_value ? fmtINR(o.project_value) : '—'}</td>
                      <td className="px-3 py-2.5">
                        <div className="flex items-center gap-1.5 justify-center">
                          <div className="w-14 h-1.5 bg-zinc-100 rounded overflow-hidden"><div className="h-full bg-blue-500" style={{ width: `${o.completion_pct || 0}%` }} /></div>
                          <span className="text-zinc-500">{o.completion_pct || 0}%</span>
                        </div>
                      </td>
                      <td className="px-3 py-2.5 text-right text-zinc-700">{o.outstanding_receivable ? fmtINR(o.outstanding_receivable) : '—'}</td>
                      <td className="px-3 py-2.5 text-center">{o.open_alerts_count > 0 ? <span className="bg-red-100 text-red-700 px-1.5 py-0.5 rounded font-bold" data-testid={`alerts-${project.id}`}>{o.open_alerts_count}</span> : <span className="text-zinc-300">0</span>}</td>
                      <td className="px-3 py-2.5 text-center">
                        <span className="inline-flex items-center gap-1.5" title={o.margin_pct != null ? `${o.margin_pct}%` : 'No cost data'} data-testid={`margin-${project.id}`}>
                          <span className={`w-2.5 h-2.5 rounded-full ${HEALTH_DOT[o.margin_health] || HEALTH_DOT.unknown}`} />
                          <span className="text-zinc-500">{o.margin_pct != null ? `${o.margin_pct}%` : '—'}</span>
                        </span>
                      </td>
                      <td className="px-3 py-2.5 text-center text-zinc-500">{o.days_since_last_dpr != null ? `${o.days_since_last_dpr}d` : '—'}</td>
                      <td className="px-3 py-2.5">
                        <span className={`text-[11px] font-medium px-1.5 py-0.5 border ${getStatusColor(project.status)}`}>{project.status}</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="projects-card-view">
            {sortedProjects.map((project) => (
              <div key={project.id} className="bg-white border border-zinc-200 p-5 hover:shadow-sm transition-shadow" data-testid={`project-card-${project.id}`}>
                <div className="flex items-start justify-between mb-2">
                  <FileText size={28} className="text-blue-700" />
                  <span className="text-xs text-zinc-500">{project.quote_count || 0} quotes</span>
                </div>
                <div onClick={() => navigate(`/projects/${project.id}`)} className="cursor-pointer">
                  <h3 className="text-sm font-semibold text-zinc-800">{project.pi_number}</h3>
                  <p className="text-xs font-mono text-blue-700 mt-0.5">
                    {project.project_code || <span className="text-zinc-400">Code: Pending</span>}
                  </p>
                  <p className="text-sm text-zinc-600 mt-1 font-medium">{project.client_name}</p>
                  {project.client_legal_name && project.client_legal_name !== project.client_name && (
                    <p className="text-xs text-zinc-500 mt-0.5 flex items-center gap-1"><Buildings size={12} /> {project.client_legal_name}</p>
                  )}
                  {project.brand_name && <p className="text-xs text-blue-600 mt-0.5">Brand: {project.brand_name}</p>}
                  {project.company_name && <p className="text-xs text-zinc-500 mt-0.5">{project.company_name}</p>}
                  {project.description && <p className="text-xs text-zinc-500 line-clamp-2 mt-1">{project.description}</p>}
                </div>
                <div className="mt-3 pt-3 border-t border-zinc-200" onClick={(e) => e.stopPropagation()}>
                  <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500 mb-1.5 block">Status</Label>
                  <select
                    value={project.status === 'Awaiting confirmation' ? 'Draft' : project.status}
                    onChange={(e) => handleStatusUpdate(project.id, e.target.value)}
                    className={`w-full text-xs font-medium border rounded-none p-1.5 ${getStatusColor(project.status)}`}
                    data-testid={`status-select-${project.id}`}
                    disabled={project.status === 'Cancelled'}
                  >
                    <option value="Draft">Draft</option>
                    <option value="Interim Approval">Interim Approval</option>
                    <option value="Confirmed">Confirmed</option>
                    <option value="On Hold">On Hold</option>
                    <option value="Client Not Reverting">Client Not Reverting</option>
                    <option value="Cancelled">Cancelled</option>
                  </select>
                  {project.status === 'Confirmed' && (
                    <p className="text-xs text-green-700 mt-1 flex items-center gap-1"><Lock size={10} /> Locked</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default Dashboard;
